import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApiCall from '../../../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../../../hooks/useGetGeneralAppData';
import ClientUsersService from '../../../../../../services/ClientUsersService';
import { SelectedPermissionFromApiType, SelectedPermissionType } from '../../types';

interface IUseLoadForm {
  setIsLoadLoading: Dispatch<SetStateAction<boolean>>,
  setCpf: Dispatch<SetStateAction<string>>,
  setEmail: Dispatch<SetStateAction<string>>,
  setName: Dispatch<SetStateAction<string>>,
  setType: Dispatch<SetStateAction<{ value: number, label: string }>>,
  setSelectedPermissions: Dispatch<SetStateAction<SelectedPermissionType[]>>,
  adminPermissions: SelectedPermissionType[],
  defaultPermissions: SelectedPermissionType[],
  getPermissionsOptions: () => Promise<void>,
}

export default function useLoadForm({
  setIsLoadLoading,
  setCpf,
  setEmail,
  setName,
  setType,
  setSelectedPermissions,
  adminPermissions,
  defaultPermissions,
  getPermissionsOptions,
}: IUseLoadForm) {
  const [typeOptions, setTypeOptions] = useState([
    { value: 1, label: 'Administrador'},
    { value: 2, label: 'Padrão' },
    { value: 3, label: 'Motorista' },
  ]);

  const { id } = useParams();
  const { apiCall } = useApiCall();
  const navigate = useNavigate();
  const { userCode, userProfile, companyCode } = useGetGeneralAppData();

  const getUserData = useCallback(async () => {
    await apiCall({
      apiToCall: ClientUsersService.getClientUser,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        codUsuAcesso: id,
      },
      onStartLoad: () => setIsLoadLoading(true),
      onEndLoad: () => setIsLoadLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível recuperar os dados do usuário. Por favor, tente novamente (${response.msg})`);
          navigate('/clientUsers');
          return;
        }
        if (response.tipoContatoCod !== 3) {
          setTypeOptions([
            { value: 1, label: 'Administrador'},
            { value: 2, label: 'Padrão' },
          ]);
        }
        const typeObjectLiterals: {[key: number]: { value: number, label: string }} = {
          1: { value: 1, label: 'Administrador' },
          2: { value: 2, label: 'Padrão' },
          3: { value: 3, label: 'Motorista'},
        };
        const userPermissions: SelectedPermissionFromApiType[] = response.permissoesNav;
        const mappedUserPermissions = userPermissions?.map((permission) => ({
          pageCode: permission.codPag,
          pageLabel: permission.pagina,
          add: permission.incluir,
          view: permission.vizualizar,
          update: permission.alterar,
          download: permission.imprimir,
          delete: permission.excluir,
        }));
        setCpf(response.cpf);
        setEmail(response.email);
        setName(response.nome);
        setType(typeObjectLiterals[response.tipoContatoCod] ?? 'Padrão');
        if (!mappedUserPermissions || mappedUserPermissions?.length === 0) {
          setSelectedPermissions(response.tipoContatoCod === 1 ? adminPermissions : defaultPermissions);
          return;
        }
        setSelectedPermissions(mappedUserPermissions);
      },
      catchMessage: 'Não foi possível carregar os dados do usuário',
      catchAction: () => {
        navigate('/clientUsers');
      }
    });
  }, [adminPermissions, apiCall, companyCode, defaultPermissions, id, navigate, setCpf, setEmail, setIsLoadLoading, setName, setSelectedPermissions, setType, userCode, userProfile]);

  const loadForm = useCallback(async () => {
    await flushSync(async () => {
      await getPermissionsOptions();
    });
  }, [getPermissionsOptions]);

  useEffect(() => {
    loadForm();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) {
      getUserData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminPermissions, defaultPermissions]);

  return { typeOptions };
}
